import React, { useEffect, useState } from 'react';
import { responses, questions } from '../services/api';
import { Box, Container, Typography, Paper } from '@mui/material';
import { CustomRadarChart } from './RadarChart';
import { theme } from '../theme';

interface RadarDataPoint {
  question: string;
  value: number;
}

interface Response {
  question_text: string;
  question_type: string;
  question_category: string;
  rating_value: number;
  created_at: string;
}

interface Question {
  question_type: { name: string };
  category: string;
}

const findStrengthAndOpportunity = (data: RadarDataPoint[]) => {
  if (!data.length) return { strength: null, opportunity: null };
  
  const sorted = [...data].sort((a, b) => b.value - a.value);
  return {
    strength: sorted[0],
    opportunity: sorted[sorted.length - 1]
  };
};

const ResponseHistory: React.FC = () => {
  const [hasEnoughResponses, setHasEnoughResponses] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const historyResponse = await responses.getHistory();
        const ratingResponses = historyResponse.data.filter(
          (resp: Response) => resp.question_type === 'rating'
        );

        if (ratingResponses.length === 0) {
          setHasEnoughResponses(false);
          setIsLoading(false);
          return;
        }

        setHasEnoughResponses(true);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0A0B1E 0%, #1A1B3E 100%)',
      padding: theme.spacing(4),
    }}>
      <Container maxWidth="md">
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            mb: 4
          }}
        >
          Your Pillar Profile
        </Typography>

        {isLoading ? (
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
              Analyzing...
            </Typography>
          </Paper>
        ) : hasEnoughResponses ? (
          <>
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Progress Overview
              </Typography>
              <CustomRadarChart />
            </Box>
          </>
        ) : (
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
              Please fill out the initial intake questions in order to see your analytics!
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default ResponseHistory; 